'use client';

/**
 * DualPrice Component
 *
 * Displays a price in CLP with USD equivalent, or USD only.
 * Uses real-time exchange rate from Firestore.
 *
 * @example
 * ```tsx
 * // Basic usage (shows both currencies)
 * <DualPrice amountCLP={334900} />
 * // -> "CLP $334,900 ≈ USD $372.11"
 *
 * // USD only mode (hides CLP)
 * <DualPrice amountCLP={334900} showCLP={false} />
 * // -> "USD $372.11"
 *
 * // Compact mode
 * <DualPrice amountCLP={334900} size="sm" />
 * // -> "$334,900 ≈ $372"
 *
 * // Spanish locale
 * <DualPrice amountCLP={334900} locale="es" />
 * // -> "CLP $334.900 ≈ USD $372,11"
 * ```
 */

import { useMemo } from 'react';
import { useExchangeRate } from '@/lib/hooks/useExchangeRate';
import { useSafeTranslations } from '@/hooks/useSafeTranslations';
import styles from './DualPrice.module.css';

// ============================================================================
// Types
// ============================================================================

export interface DualPriceProps {
  /** Amount in CLP (Chilean Pesos) */
  amountCLP: number;
  /** Whether to show USD equivalent (default: true) */
  showUSD?: boolean;
  /** Whether to show CLP price (default: true). Set to false to show only USD */
  showCLP?: boolean;
  /** Locale for number formatting */
  locale?: 'en' | 'es' | 'pt';
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Optional className for styling */
  className?: string;
  /** Show currency codes (CLP/USD) */
  showCurrencyCode?: boolean;
  /** Show approximate symbol (≈) */
  showApproxSymbol?: boolean;
  /** Vertical layout (stacked) */
  stacked?: boolean;
  /** Hide cents in USD (round to whole number) */
  roundUSD?: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format CLP amount
 */
function getIntlLocale(locale: 'en' | 'es' | 'pt'): string {
  return locale === 'es' ? 'es-CL' : locale === 'pt' ? 'pt-BR' : 'en-US';
}

function formatCLP(amount: number, locale: 'en' | 'es' | 'pt'): string {
  return new Intl.NumberFormat(getIntlLocale(locale), {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format USD amount
 */
function formatUSD(amount: number, locale: 'en' | 'es' | 'pt', round: boolean): string {
  return new Intl.NumberFormat(getIntlLocale(locale), {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: round ? 0 : 2,
    maximumFractionDigits: round ? 0 : 2,
  }).format(amount);
}

// ============================================================================
// Component
// ============================================================================

export function DualPrice({
  amountCLP,
  showUSD = true,
  showCLP = true,
  locale = 'en',
  size = 'md',
  className,
  showCurrencyCode = true,
  showApproxSymbol = true,
  stacked = false,
  roundUSD = false,
}: DualPriceProps) {
  const { loading, convertCLPtoUSD } = useExchangeRate();

  // Calculate USD equivalent
  const usdAmount = useMemo(() => {
    return convertCLPtoUSD(amountCLP);
  }, [amountCLP, convertCLPtoUSD]);

  // Format prices
  const formattedCLP = useMemo(() => {
    if (showCurrencyCode) {
      return formatCLP(amountCLP, locale);
    }
    // Without currency code, just format the number
    return new Intl.NumberFormat(getIntlLocale(locale), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amountCLP);
  }, [amountCLP, locale, showCurrencyCode]);

  const formattedUSD = useMemo(() => {
    if (showCurrencyCode) {
      return formatUSD(usdAmount, locale, roundUSD);
    }
    // Without currency code, just format the number
    return new Intl.NumberFormat(getIntlLocale(locale), {
      minimumFractionDigits: roundUSD ? 0 : 2,
      maximumFractionDigits: roundUSD ? 0 : 2,
    }).format(usdAmount);
  }, [usdAmount, locale, showCurrencyCode, roundUSD]);

  // Build class names
  const containerClasses = [
    styles.container,
    styles[size],
    stacked ? styles.stacked : styles.inline,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // If only showing USD (showCLP=false), use USD styling as primary
  if (!showCLP && showUSD) {
    return (
      <span className={containerClasses}>
        <span className={styles.clpPrice}>
          {loading ? (
            <span className={styles.loading}>...</span>
          ) : (
            <>
              {showCurrencyCode && <span className={styles.currencyCode}>USD </span>}
              {formattedUSD.replace('USD', '').trim()}
            </>
          )}
        </span>
      </span>
    );
  }

  return (
    <span className={containerClasses}>
      {showCLP && (
        <span className={styles.clpPrice}>
          {showCurrencyCode && <span className={styles.currencyCode}>CLP </span>}
          {formattedCLP.replace('CLP', '').trim()}
        </span>
      )}

      {showUSD && showCLP && (
        <>
          {showApproxSymbol && <span className={styles.separator}> ≈ </span>}
          <span className={styles.usdPrice}>
            {loading ? (
              <span className={styles.loading}>...</span>
            ) : (
              <>
                {showCurrencyCode && <span className={styles.currencyCode}>USD </span>}
                {formattedUSD.replace('USD', '').trim()}
              </>
            )}
          </span>
        </>
      )}
    </span>
  );
}

/**
 * Simple USD conversion display (for checkout/payment)
 * Shows: "USD $372.11 will be charged"
 */
export function USDChargeAmount({
  amountCLP,
  locale = 'en',
  className,
}: {
  amountCLP: number;
  locale?: 'en' | 'es' | 'pt';
  className?: string;
}) {
  const { convertCLPtoUSD, loading, rate } = useExchangeRate();
  const usdAmount = convertCLPtoUSD(amountCLP);

  const t = useSafeTranslations('dualPrice');

  if (loading) {
    return <span className={className}>{t('loading')}</span>;
  }

  return (
    <span className={`${styles.chargeAmount} ${className || ''}`}>
      <span className={styles.usdAmount}>
        {formatUSD(usdAmount, locale, false)}
      </span>
      <span className={styles.chargeLabel}>{t('willBeCharged')}</span>
      <span className={styles.rateInfo}>
        ({t('exchangeRate')}: 1 USD = {rate.toFixed(2)} CLP)
      </span>
    </span>
  );
}

/**
 * Conversion info tooltip/popover content
 */
export function ConversionInfo({
  amountCLP,
  locale = 'en',
}: {
  amountCLP: number;
  locale?: 'en' | 'es' | 'pt';
}) {
  const { rate, convertCLPtoUSD, source, isStale, fetchedAt } = useExchangeRate();
  const usdAmount = convertCLPtoUSD(amountCLP);

  const t = useSafeTranslations('dualPrice');

  return (
    <div className={styles.conversionInfo}>
      <h4 className={styles.conversionTitle}>{t('conversion')}</h4>
      <dl className={styles.conversionDetails}>
        <div className={styles.conversionRow}>
          <dt>{t('originalAmount')}:</dt>
          <dd>{formatCLP(amountCLP, locale)}</dd>
        </div>
        <div className={styles.conversionRow}>
          <dt>{t('exchangeRate')}:</dt>
          <dd>1 USD = {rate.toFixed(2)} CLP</dd>
        </div>
        <div className={styles.conversionRow}>
          <dt>{t('chargedAmount')}:</dt>
          <dd className={styles.highlight}>{formatUSD(usdAmount, locale, false)}</dd>
        </div>
        <div className={styles.conversionRow}>
          <dt>{t('rateSource')}:</dt>
          <dd>{source}</dd>
        </div>
        {fetchedAt && (
          <div className={styles.conversionRow}>
            <dt>{t('lastUpdated')}:</dt>
            <dd>{fetchedAt.toLocaleString(getIntlLocale(locale))}</dd>
          </div>
        )}
        {isStale && (
          <div className={styles.staleWarning}>
            {t('staleWarning')}
          </div>
        )}
      </dl>
    </div>
  );
}

export default DualPrice;
